import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LockKeyhole } from "lucide-react";
import { adminLogin, setToken, formatApiErrorDetail } from "@/api";
import { LOGO_URL } from "@/components/Navbar";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await adminLogin(email, password);
      setToken(res.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-4 pt-20">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 md:p-10"
      >
        <div className="flex flex-col items-center mb-8">
          <span className="inline-flex items-center rounded-xl bg-white px-3 py-1.5 mb-5">
            <img src={LOGO_URL} alt="CompCar Motors" className="h-10 w-auto" />
          </span>
          <h1 className="font-display text-2xl font-black text-white flex items-center gap-2">
            <LockKeyhole size={20} className="text-[#F57C00]" /> Admin Sign In
          </h1>
          <p className="text-sm text-slate-400 mt-1">Manage vehicles and stock</p>
        </div>

        <form onSubmit={submit} data-testid="admin-login-form" className="space-y-5">
          <div>
            <label className="input-label" htmlFor="adm-email">Email</label>
            <input
              id="adm-email"
              data-testid="admin-email-input"
              type="email"
              required
              className="input-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@compcarmotors.co.za"
            />
          </div>
          <div>
            <label className="input-label" htmlFor="adm-password">Password</label>
            <input
              id="adm-password"
              data-testid="admin-password-input"
              type="password"
              required
              className="input-field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-md px-4 py-3">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="text-center mt-6">
          <Link to="/" data-testid="admin-login-back-link" className="text-xs text-slate-500 hover:text-sky-300 transition-colors">
            ← Back to website
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
